import React from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import type { PortalNotificationFormState } from "src/types/portalNotifications";

interface Props {
  form: PortalNotificationFormState;
}

/**
 * Static mock of how the notification will render in the Client Portal:
 * the login pop-up slide and the Notification Hub card.
 */
export const PortalNotificationPreview: React.FC<Props> = ({ form }) => {
  const headline = form.headline || "Headline";
  const body = form.body || "Body text will appear here.";

  return (
    <div className="space-y-6">
      {/* Login pop-up mock */}
      <div>
        <p className="mb-2 text-xs font-extrabold uppercase tracking-wide text-[#9CA3AF]">
          Login pop-up
        </p>
        <div className="rounded-2xl border-2 border-[#E5E5E5] bg-[#F3F4F6] p-4">
          <div className="mx-auto max-w-sm rounded-2xl border-2 border-[#E5E5E5] bg-white p-5 shadow-lg">
            <div className="mb-3 flex items-start justify-between">
              <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-indigo-600">
                {form.category}
              </span>
              <X className="h-4 w-4 text-[#9CA3AF]" />
            </div>
            <p className="text-base font-extrabold text-[#1F1F1F]">
              {headline}
            </p>
            <p className="mt-1.5 whitespace-pre-line text-sm text-[#6B7280]">
              {body}
            </p>
            {form.ctaLabel && (
              <p className="mt-3 text-sm font-bold text-teal-600">
                {form.ctaLabel} →
              </p>
            )}
            <div className="mt-4 flex items-center justify-between border-t border-[#F3F4F6] pt-3">
              <div className="flex items-center gap-1 text-xs text-[#9CA3AF]">
                <ChevronLeft className="h-3.5 w-3.5" />
                Back
              </div>
              <span className="text-xs font-semibold text-[#9CA3AF]">
                1 of 1
              </span>
              <div className="flex items-center gap-1 text-xs font-bold text-teal-600">
                Next
                <ChevronRight className="h-3.5 w-3.5" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hub card mock */}
      <div>
        <p className="mb-2 text-xs font-extrabold uppercase tracking-wide text-[#9CA3AF]">
          Notification Hub card
        </p>
        <div className="rounded-2xl border-2 border-[#E5E5E5] bg-[#F3F4F6] p-4">
          <div className="rounded-xl border border-[#E5E5E5] bg-white p-4">
            <div className="mb-1 flex items-center justify-between gap-2">
              <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-indigo-600">
                {form.category}
              </span>
              <span className="text-[11px] text-[#9CA3AF]">
                Sep 30, 10:00 AM
              </span>
            </div>
            <p className="text-sm font-bold text-[#1F1F1F]">{headline}</p>
            <p className="mt-0.5 line-clamp-2 whitespace-pre-line text-xs text-[#6B7280]">
              {body}
            </p>
            {form.ctaLabel && (
              <p className="mt-2 text-xs font-bold text-teal-600">
                {form.ctaLabel} →
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PortalNotificationPreview;
